import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FRAU_MEETS_BOERSE_CONFIG } from '@/consts/frauMeetsBoerse';
import type { FmbResourceLink } from '@/consts/frauMeetsBoerse';
import FmbLayout, { FMB_SERIF as SERIF, fmbHomePath } from '@/components/frauMeetsBoerse/FmbLayout';
import FmbHeroVideo from '@/components/frauMeetsBoerse/FmbHeroVideo';
import FmbImageSlot from '@/components/frauMeetsBoerse/FmbImageSlot';
import FmbCheckList from '@/components/frauMeetsBoerse/FmbCheckList';
import FmbFaq from '@/components/frauMeetsBoerse/FmbFaq';
import FmbTestimonialMarquee from '@/components/frauMeetsBoerse/FmbTestimonialMarquee';

const config = FRAU_MEETS_BOERSE_CONFIG;
const videosPage = config.videosPage;
const offer = videosPage.offer;
const stepOne = videosPage.stepOne;

/** Kleines Uppercase-Label über einer Überschrift — auf dieser Seite statt der Sektionsnummern */
const LABEL = 'text-[13px] font-bold tracking-[0.14em] uppercase text-[var(--fmb-label)]';

interface SectionIntroProps {
  kicker: string;
  heading: string;
  /** Kursiver Akzent-Teil hinter der Überschrift */
  headingAccent?: string;
  subline?: string;
}

function SectionIntro({ kicker, heading, headingAccent, subline }: SectionIntroProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className={LABEL}>{kicker}</div>
      <h2 className={`${SERIF} m-0 max-w-[24ch] text-[clamp(34px,5vw,44px)] leading-[1.1] font-normal text-balance`}>
        {heading}
        {headingAccent !== undefined && <em className="text-[var(--fmb-accent)]">{headingAccent}</em>}
      </h2>
      {subline !== undefined && (
        <p className="m-0 max-w-[56ch] text-[17px] leading-[1.7] text-pretty text-[var(--fmb-muted)]">{subline}</p>
      )}
    </div>
  );
}

interface PurchaseCtaProps {
  label: string;
  /** Kleingedrucktes unter dem Button — null lässt die Zeile weg */
  microcopy: string | null;
  /** 'accent': Akzent-Button auf hellem Grund · 'onAccent': weißer Button im Akzent-Banner */
  variant?: 'accent' | 'onAccent';
}

/**
 * Kauf-Button des kompletten Workshops. Immer ein echtes <a>: registerUrl zeigt
 * in Produktion auf die Börsencampus-Domain (Registrierung → Stripe-Checkout),
 * ein Router-Link würde dort ins Leere greifen.
 */
function PurchaseCta({ label, microcopy, variant = 'accent' }: PurchaseCtaProps) {
  const button =
    variant === 'accent'
      ? 'bg-[var(--fmb-accent)] px-10 py-[18px] font-bold text-white shadow-[0_10px_24px_var(--fmb-shadow-cta)] hover:bg-[var(--fmb-accent-dark)]'
      : 'bg-white px-11 py-[18px] font-extrabold text-[var(--fmb-accent)] hover:bg-[var(--fmb-cta-button-hover)]';
  const note = variant === 'accent' ? 'text-[var(--fmb-faint)]' : 'text-[var(--fmb-cta-ps)]';

  return (
    <div className="flex flex-col items-center gap-2.5">
      <a
        href={config.registerUrl}
        className={`max-w-full cursor-pointer rounded-full text-center text-[17px] leading-[1.35] text-balance no-underline transition-colors ${button}`}
      >
        {label}
      </a>
      {microcopy !== null && <div className={`max-w-[46ch] text-[13px] leading-[1.5] ${note}`}>{microcopy}</div>}
    </div>
  );
}

function GiftHeader() {
  return (
    <section className="mx-auto flex max-w-[960px] flex-col items-center gap-7 px-[clamp(20px,5vw,40px)] pt-8 pb-[clamp(48px,8vw,80px)] text-center">
      <Link
        to={fmbHomePath()}
        className="self-start text-[14px] text-[var(--fmb-faint)] no-underline transition-colors hover:text-[var(--fmb-accent)]"
      >
        {videosPage.backLabel}
      </Link>
      <div className="flex items-center gap-2.5 rounded-full border border-[var(--fmb-badge-border)] px-[18px] py-2 text-[13px] font-bold tracking-[0.12em] uppercase text-[var(--fmb-accent)]">
        <span className="h-2 w-2 shrink-0 rounded-full bg-[var(--fmb-accent)]" />
        {videosPage.badge}
      </div>
      <h1 className={`${SERIF} m-0 max-w-[18ch] text-[clamp(40px,6.5vw,64px)] leading-[1.06] font-normal text-balance`}>
        {videosPage.titleLeading}
        <em className="text-[var(--fmb-accent)]">{videosPage.titleAccent}</em>
      </h1>
      <p className="m-0 max-w-[56ch] text-[clamp(17px,2.2vw,20px)] leading-[1.6] text-pretty text-[var(--fmb-muted)]">
        {videosPage.paragraph}
      </p>
      <div className="text-[13px] tracking-[0.02em] text-[var(--fmb-faint)]">{videosPage.microcopy}</div>
    </section>
  );
}

interface StepOneResourceLinkProps {
  resource: FmbResourceLink;
  /** Vollständige Button-Klassen — der Aufrufer entscheidet über primär/sekundär */
  className: string;
}

/**
 * Link auf ein Arbeitsmaterial. Eigene Dateien aus public/fmb/ bekommen das
 * download-Attribut (greift nur same-origin), externe Tools öffnen in einem
 * neuen Tab — der Hinweis darauf steckt im aria-label.
 */
function StepOneResourceLink({ resource, className }: StepOneResourceLinkProps) {
  if (resource.kind === 'download') {
    return (
      <a href={resource.href} download={resource.downloadName ?? ''} className={className}>
        {resource.label}
      </a>
    );
  }

  return (
    <a
      href={resource.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${resource.label} (öffnet in neuem Tab)`}
      className={className}
    >
      {resource.label}
      <span aria-hidden="true"> ↗</span>
    </a>
  );
}

/** Aufgaben aus Schritt 1 — steht über der Videoliste */
function StepOneIntro() {
  return (
    <section className="mx-auto flex w-full max-w-[960px] flex-col gap-6 px-[clamp(20px,5vw,40px)] pb-[clamp(48px,8vw,80px)]">
      <h2 className={`${SERIF} m-0 max-w-[24ch] self-center text-center text-[clamp(34px,5vw,44px)] leading-[1.1] font-normal text-balance`}>
        {stepOne.heading}
      </h2>
      <FmbCheckList items={stepOne.items} />
    </section>
  );
}

/** Arbeitsmaterial zu Schritt 1 — steht unter dem Video mit showStepOneResources */
function StepOneResources() {
  return (
    <div className="flex w-full flex-col items-center gap-6 text-center">
      <p className="m-0 max-w-[62ch] text-[16px] leading-[1.65] text-pretty text-[var(--fmb-muted)]">
        {stepOne.note}
      </p>
      <div className="flex flex-col items-center gap-4">
        <StepOneResourceLink
          resource={stepOne.workbook}
          className="max-w-full cursor-pointer rounded-full bg-[var(--fmb-accent)] px-8 py-4 text-center text-[16px] leading-[1.35] font-bold text-balance text-white no-underline shadow-[0_10px_24px_var(--fmb-shadow-cta)] transition-colors hover:bg-[var(--fmb-accent-dark)]"
        />
        <div className="flex flex-wrap items-center justify-center gap-3">
          {stepOne.tableLinks.map((link) => (
            <StepOneResourceLink
              key={link.href}
              resource={link}
              className="max-w-full cursor-pointer rounded-full border border-[var(--fmb-badge-border)] bg-white px-6 py-[13px] text-center text-[15px] leading-[1.35] font-bold text-[var(--fmb-accent)] no-underline transition-colors hover:border-[var(--fmb-accent)] hover:bg-[var(--fmb-section-bg)]"
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function WorkshopSteps() {
  return (
    <section className="mx-auto flex w-full max-w-[900px] flex-col gap-10 px-[clamp(20px,5vw,40px)] pb-[clamp(56px,9vw,88px)]">
      <SectionIntro
        kicker={offer.workshop.kicker}
        heading={offer.workshop.headingLeading}
        headingAccent={offer.workshop.headingAccent}
        subline={offer.workshop.subline}
      />
      <div className="flex flex-col gap-4">
        {offer.workshop.steps.map((step) => (
          <div
            key={step.label}
            className="flex flex-col gap-2.5 rounded-[14px] border border-[var(--fmb-border-card)] bg-white px-[26px] py-6"
          >
            <div className="flex flex-wrap items-center gap-3">
              <div className={LABEL}>{step.label}</div>
              {step.note !== null && (
                <div className="rounded-full border border-[var(--fmb-badge-border)] px-3 py-1 text-[12px] font-bold tracking-[0.1em] uppercase text-[var(--fmb-accent)]">
                  {step.note}
                </div>
              )}
            </div>
            <h3 className="m-0 text-[22px] leading-[1.3] font-bold">{step.title}</h3>
            <p className="m-0 text-[16px] leading-[1.65] text-pretty text-[var(--fmb-card-text)]">{step.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function MiriamStory() {
  return (
    <section className="border-y border-[var(--fmb-border)] bg-[var(--fmb-section-bg)]">
      <div className="mx-auto grid max-w-[1040px] grid-cols-[repeat(auto-fit,minmax(320px,1fr))] items-center gap-y-12 gap-x-[clamp(40px,6vw,72px)] px-[clamp(20px,5vw,40px)] py-[clamp(56px,10vw,96px)]">
        <FmbImageSlot image={offer.miriam.image} fit="contain" className="h-[480px] w-full rounded-[18px]" />
        <div className="flex flex-col gap-5">
          <SectionIntro
            kicker={offer.miriam.kicker}
            heading={offer.miriam.headingLeading}
            headingAccent={offer.miriam.headingAccent}
          />
          <p className="m-0 max-w-[52ch] text-[17px] leading-[1.7] text-pretty text-[var(--fmb-muted)]">
            {offer.miriam.paragraph}
          </p>
        </div>
      </div>
    </section>
  );
}

function Deliverables() {
  return (
    <section className="mx-auto flex w-full max-w-[820px] flex-col gap-10 px-[clamp(20px,5vw,40px)] pt-[clamp(56px,9vw,88px)] pb-[clamp(48px,8vw,80px)]">
      <SectionIntro kicker={offer.deliverables.kicker} heading={offer.deliverables.heading} />
      <FmbCheckList items={offer.deliverables.items} />
    </section>
  );
}

function Price() {
  return (
    <section className="mx-auto w-full max-w-[820px] px-[clamp(20px,5vw,40px)] pb-[clamp(56px,9vw,88px)]">
      <div className="flex flex-col items-center gap-6 rounded-[18px] border border-[var(--fmb-border-card)] bg-white px-[clamp(24px,5vw,48px)] py-[clamp(36px,6vw,56px)] text-center shadow-[0_16px_40px_var(--fmb-shadow-card)]">
        <div className={LABEL}>{offer.price.kicker}</div>
        <h2 className={`${SERIF} m-0 text-[clamp(40px,6vw,56px)] leading-[1.05] font-normal`}>
          {offer.price.headingLeading}
          <em className="text-[var(--fmb-accent)]">{offer.price.headingAccent}</em>
        </h2>
        <p className="m-0 max-w-[52ch] text-[17px] leading-[1.7] text-pretty text-[var(--fmb-muted)]">
          {offer.price.paragraph}
        </p>
        <div className="flex w-full flex-col items-center gap-3 border-t border-[var(--fmb-border-warm)] pt-6">
          <div className={LABEL}>{offer.price.contextLabel}</div>
          <p className="m-0 max-w-[54ch] text-[16px] leading-[1.7] text-pretty text-[var(--fmb-muted)]">
            {offer.price.contextParagraph}
          </p>
        </div>
        <PurchaseCta label={offer.price.ctaLabel} microcopy={offer.price.microcopy} />
      </div>
    </section>
  );
}

function SocialProof() {
  return (
    <section className="border-y border-[var(--fmb-border)] bg-[var(--fmb-section-bg)]">
      <div className="mx-auto flex max-w-[1100px] flex-col gap-12 px-[clamp(20px,5vw,40px)] py-[clamp(56px,10vw,96px)]">
        <div className="flex flex-wrap items-baseline justify-between gap-6">
          <SectionIntro
            kicker={offer.proof.kicker}
            heading={offer.proof.headingLeading}
            headingAccent={offer.proof.headingAccent}
          />
          <div className="text-[14px] text-[var(--fmb-faint)]">{config.testimonials.ratingNote}</div>
        </div>
        <FmbTestimonialMarquee video={config.testimonials.video} items={config.testimonials.items} />
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="mx-auto flex w-full max-w-[800px] flex-col gap-10 px-[clamp(20px,5vw,40px)] pt-[clamp(56px,9vw,88px)] pb-[clamp(48px,8vw,80px)]">
      <h2 className={`${SERIF} m-0 text-[clamp(34px,5vw,44px)] leading-[1.1] font-normal text-balance`}>
        {offer.faq.heading}
      </h2>
      <FmbFaq items={offer.faq.items} />
    </section>
  );
}

function FinalCta() {
  return (
    <section className="mx-auto max-w-[1100px] px-[clamp(20px,5vw,40px)] pb-[clamp(72px,11vw,112px)]">
      <div className="flex flex-col items-center gap-6 rounded-3xl bg-[var(--fmb-accent)] px-[clamp(28px,6vw,64px)] py-[clamp(48px,8vw,80px)] text-center text-white">
        <div className="text-[13px] font-bold tracking-[0.14em] uppercase text-[var(--fmb-cta-kicker)]">
          {offer.cta.kicker}
        </div>
        <h2 className={`${SERIF} m-0 max-w-[20ch] text-[clamp(34px,5.5vw,48px)] leading-[1.1] font-normal text-balance`}>
          {offer.cta.headingLeading}
          <em>{offer.cta.headingAccent}</em>
        </h2>
        <p className="m-0 max-w-[52ch] text-[18px] leading-[1.6] text-pretty text-[var(--fmb-cta-text)]">
          {offer.cta.paragraph}
        </p>
        <PurchaseCta label={offer.cta.ctaLabel} microcopy={offer.cta.microcopy} variant="onAccent" />
      </div>
    </section>
  );
}

/**
 * Unterseite mit den geschenkten Umsetzungsvideos (Schritt 1 des
 * 4-Schritte-Umsetzungsworkshops) und dem Angebot für die Schritte 2 bis 4.
 * Die URL bleibt aus historischen Gründen '/webinar' (bereits verlinkt und
 * indexiert) — das Webinar selbst läuft inzwischen direkt im Hero der
 * Landingpage, deren CTA-Buttons alle hierher führen.
 *
 * Dramaturgie: Geschenk → Schritt 1 (Aufgaben) → Videos (Material unter dem
 * Willkommensvideo) → Brücke →
 * Workshop → Miriam → Lieferumfang → Preis → Stimmen → FAQ → Abschluss-CTA.
 * Der Header-CTA ist ausgeblendet, der Zurück-Link bleibt bewusst klein — kein
 * prominenter Ausgang über dem Geschenk.
 *
 * Die Videos werden per Click-to-Play geladen (keine zwölf Iframes beim
 * Seitenaufruf); solange eine embedUrl null ist, erscheint der beschriftete
 * Platzhalter. Alle drei Kauf-Buttons zeigen auf config.registerUrl — die
 * Börsencampus-Registrierung, die anschließend automatisch den Stripe-Checkout
 * für den kompletten Workshop startet.
 */
export default function FmbVideosPage() {
  // Akkordeon wie im FAQ: initial ist nur das erste Video aufgeklappt,
  // maximal eines gleichzeitig offen — Zuklappen unmountet den Player
  // und stoppt damit auch die Wiedergabe.
  const [openIndex, setOpenIndex] = useState<number>(0);
  const articleRefs = useRef<(HTMLElement | null)[]>([]);
  const bridgeRef = useRef<HTMLElement | null>(null);

  const openAndScrollTo = (index: number) => {
    setOpenIndex(index);
    // Erst nach dem Rendern scrollen — durch das Zuklappen des vorherigen
    // Videos verschiebt sich das Layout.
    requestAnimationFrame(() => {
      articleRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // Nach dem letzten Video geht es zur Brücke direkt unter der Videoliste —
  // nicht zum Abschluss-CTA, der alles überspringen würde, was das Angebot erklärt.
  const scrollToBridge = () => {
    bridgeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <FmbLayout title={`Umsetzungsvideos – ${config.pageTitle}`} showHeaderCta={false}>
      <GiftHeader />

      <StepOneIntro />

      <section className="mx-auto flex w-full max-w-[960px] flex-col gap-7 px-[clamp(20px,5vw,40px)] pb-[clamp(48px,8vw,80px)]">
        <div className="text-[15px] font-bold tracking-[0.02em] text-[var(--fmb-accent)]">{videosPage.anchorLine}</div>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div className={LABEL}>{videosPage.stepLabel}</div>
          <div className="text-[14px] text-[var(--fmb-faint)]">{videosPage.stepNote}</div>
        </div>
        <div className="flex flex-col border-t border-[var(--fmb-ink)]">
          {videosPage.videos.map((video, index) => {
            const isOpen = openIndex === index;
            const isLast = index === videosPage.videos.length - 1;

            return (
              <article
                key={video.title}
                ref={(el) => {
                  articleRefs.current[index] = el;
                }}
                className="scroll-mt-6 border-b border-[var(--fmb-border-warm)]"
              >
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  className="flex w-full cursor-pointer items-center justify-between gap-6 px-1 py-6 text-left"
                >
                  <h2 className="m-0 text-[clamp(18px,2.4vw,22px)] leading-[1.35] font-bold">{video.title}</h2>
                  <span className={`${SERIF} shrink-0 text-[28px] text-[var(--fmb-accent)]`}>{isOpen ? '–' : '+'}</span>
                </button>
                {isOpen && (
                  <div className="flex flex-col items-center gap-6 px-1 pb-8">
                    <FmbHeroVideo
                      thumbnail={video.thumbnail}
                      embedUrl={video.embedUrl}
                      ariaLabel={`${video.title} abspielen`}
                      className="aspect-video w-full rounded-[18px] shadow-[0_16px_40px_var(--fmb-shadow-hero)]"
                    />
                    {video.showStepOneResources === true && <StepOneResources />}
                    <button
                      type="button"
                      onClick={() => (isLast ? scrollToBridge() : openAndScrollTo(index + 1))}
                      className="cursor-pointer rounded-full bg-[var(--fmb-accent)] px-8 py-4 text-[16px] font-bold text-white transition-colors hover:bg-[var(--fmb-accent-dark)]"
                    >
                      {isLast ? videosPage.lastButtonLabel : videosPage.nextButtonLabel}
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section
        ref={bridgeRef}
        className="mx-auto flex w-full max-w-[820px] scroll-mt-6 flex-col items-center gap-6 px-[clamp(20px,5vw,40px)] pb-[clamp(56px,9vw,88px)] text-center"
      >
        <div className={LABEL}>{videosPage.bridge.kicker}</div>
        <p className="m-0 max-w-[56ch] text-[clamp(17px,2.2vw,20px)] leading-[1.6] text-pretty text-[var(--fmb-muted)]">
          {videosPage.bridge.paragraph}
        </p>
        <PurchaseCta label={videosPage.bridge.ctaLabel} microcopy={videosPage.bridge.microcopy} />
      </section>

      <WorkshopSteps />
      <MiriamStory />
      <Deliverables />
      <Price />
      <SocialProof />
      <FaqSection />
      <FinalCta />
    </FmbLayout>
  );
}
